import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import KNDetailsPopover from 'components/KNDetailsPopover/KNDetailsPopover'
import KNDetailsPopoverList from 'components/KNDetailsPopover/KNDetailsPopoverList'
import usePopoverState from 'components/KNDetailsPopover/usePopoverState'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { zonedDate } from 'global/helpers/dates'
import React, { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import StopStatusChip from 'screens/StatusManager/StopStatusChip'
import StatusDateMessage from './StatusDateMessage'
import { getTypeIcon, getTypeLabel } from './TripDetails.helpers'
import type { LegData, StopData } from './TripDetails.types'

export interface StopHeaderProps {
  leg: LegData
  stop: StopData
  size?: 'large'
}

const StopHeader = ({ leg, stop, size }: StopHeaderProps): ReactElement => {
  const { t } = useTranslation()
  const location = useLocation()
  const hasAnyStatuses = stop.statuses.length > 0
  const latestStatus = hasAnyStatuses ? stop.statuses[0] : undefined
  const statusDetailsPopover = usePopoverState()

  const TypeIcon = getTypeIcon(stop.type)

  return (
    <>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <TypeIcon sx={{ color: 'primary.light' }} />
        <Stack direction="column">
          <Stack spacing={1} direction="row" alignItems="center">
            <Typography color="primary.main" variant={size === 'large' ? 'h4' : 'h6'}>
              {getTypeLabel(stop.type)}
            </Typography>
            {latestStatus && (
              <StopStatusChip
                statusId={latestStatus.id}
                onClick={(event: React.MouseEvent<HTMLElement>): void => {
                  statusDetailsPopover.handleClick(event)
                  analyticsEvent('polestar_cs_leg_status_history_popover', [getRouteName(location.pathname)])
                }}
              />
            )}
          </Stack>
          <StatusDateMessage leg={leg} stop={stop} />
        </Stack>
      </Stack>

      {hasAnyStatuses && (
        <KNDetailsPopover open={statusDetailsPopover.open} onClose={statusDetailsPopover.handleClose} anchorEl={statusDetailsPopover.anchorEl}>
          {stop.statuses.map((status) => (
            <KNDetailsPopoverList
              key={status.id}
              items={[
                {
                  label: t('screens.cs.trip_details.status_details.status'),
                  value: t(`waypoints.statuses.${status.id}`),
                },
                {
                  label: t('screens.cs.trip_details.status_details.executed_on'),
                  value: zonedDate(status.createdAt, 'full') ?? undefined,
                },
                ...(status.comment
                  ? [
                    {
                      label: t('screens.cs.trip_details.status_details.remarks'),
                      value: status.comment,
                      stacked: true,
                    },
                  ]
                  : []),
              ]}
            />
          ))}
        </KNDetailsPopover>
      )}
    </>
  )
}

export default StopHeader
